import { useQuery, useQueries } from "react-query";
import { useParams, Link } from "react-router-dom";
import { useState } from "react";
import MainCard from "components/layout/MainCard/MainCard";
import LoadingWrapper from "components/Loading/LoadingWrapper";
import useAPI from "hooks/useAPI";
import { GlobalQueries } from "hooks/useRefreshQuery";
import DataTable from "../../components/DataTable/DataTable";
import { currencyFormat, simpleFloat } from "../../helpers/functions";
import ListBlock from "./ListBlock/ListBlock";
import Box from "@mui/material/Box";
import PrintIcon from "@mui/icons-material/Print";
import Tooltip from "@mui/material/Tooltip";
import scss from "./OrdersDetailsPage.module.scss";
import columns from "./columns.json";
import DeleteDialog from "components/Dialogs/DeleteDielog";
import MediaPrint from "./MediaPrint/MediaPrint";
import Button from "../../components/Buttons/Button/Button";
import { toast } from "react-toastify";
import PlusMinusInput from "../../components/Form/PlusMinusInput/PlusMinusInput";
import { useTranslation,  } from "react-i18next";

const OrdersDetailsPage = () => {
    const { t } = useTranslation();

    const api = useAPI();
    const { id } = useParams();
    const [selectedProducts, setSelectedProducts] = useState([]);
    const [loading, setLoading] = useState(false);

    // Load all products in the cart
    const { data, isLoading } = useQuery([GlobalQueries.Orders], () => api.get(`/previous-orders/${id}`));
    const [showDialog, setShowDialog] = useState(false);
    const rabateCalc = (item) => {
        let rabate = [];

        item?.price_campaigns.map((price_campaign) => {
            if (price_campaign?.campaign_type === "b2b_rebate") {
                rabate.push(price_campaign?.calc_name);
            }
            return null;
        });

        return rabate.join("; ");
    };

    // Show the products in the table
    let products = (data?.items ?? []).map((item) => {
        const id_product = item?.basic_data?.id_product;
        const name = item?.basic_data?.name + (item?.basic_data?.attributes_text ? " (" + item?.basic_data?.attributes_text + ")" : "");
        const code = item?.basic_data?.sku;
        const linkStyle = { color: "inherit", textDecoration: "none" };

        return {
            image: item?.basic_data?.image ? <img src={item?.basic_data?.image} style={{ maxHeight: "30px" }} alt="" /> : null,
            code: id_product ? (
                <Link to={`/katalog/artikal/${id_product}`} style={linkStyle}>
                    {code}
                </Link>
            ) : (
                code
            ),
            name: id_product ? (
                <Link to={`/katalog/artikal/${id_product}`} style={linkStyle}>
                    {name}
                </Link>
            ) : (
                name
            ),
            slug: item?.basic_data?.slug,

            id: id_product,
            priceWithOutVat: currencyFormat(item?.price?.price_with_out_vat, item?.price?.currency),
            rebate: rabateCalc(item),
            discount: "-" + currencyFormat(item?.price?.price_discount_amount, item?.price?.currency),
            price: currencyFormat(item?.price?.price_subtotal, item?.price?.currency),
            quantity: simpleFloat(item?.price?.quantity),
            total: currencyFormat(item?.price?.subtotal),
        };
    });

    const convertDate = (date) => {
        const options = {
            day: "2-digit",
            month: "2-digit",
            year: "numeric",
            hour: "numeric",
            minute: "numeric",
            hour12: false,
        };

        const dateObj = new Date(date);
        const formattedDate = dateObj
            .toLocaleString("sr-RS", options)
            .replace(/,/g, "")
            .replace(/(\d+)\D+(\d+)\D+(\d+)\D+(\d+):(\d+)/, "$1.$2.$3. $4:$5");
        return formattedDate;
    };

    const handlePrint = () => {
        window.print();
    };
    const { data: duplicatedProducts } = useQuery([data?.order?.order_token], () => api.get(`/previous-orders/duplicate/message/${data?.order?.order_token}`));

    const allowDuplicate = duplicatedProducts?.allow_duplicate;
    const message = duplicatedProducts?.message;
    const getAllProducts = (products, duplicatedProducts) => {
        products?.map((item) => {
            if (item?.allow_add) {
                setSelectedProducts((prev) => [...prev, item?.cart]);
            }
        });
    };

    // Učitavanje osnovnih podataka za sve proizvode
    const productQueries = useQueries(
        (duplicatedProducts?.items || []).map((item) => ({
            queryKey: [`Product:${item.product.id}:Basic`],
            queryFn: () => api.get(`/product-details/basic-data/${item.product.id}`).then((res) => res),
            enabled: !!duplicatedProducts,
        }))
    );

    // Extra saved delivery info (e.g. selected store, courier/pickup details) - only meaningful
    // when the provider actually populated it, unlike format_data.full_name which just repeats
    // delivery_method_name shown above it.
    const deliveryAdditionalText = data?.deliveries?.[0]?.format_data?.partial?.additional
        ?.map((row) => row.text)
        .filter(Boolean)
        .join(", ");

    return (
        <>
            <MainCard className="p">
                <div className="">
                    <div
                        className="page-content"
                        style={{
                            "@media print": {
                                padding: "0 !important",
                            },
                        }}
                    >
                        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
                            <h4 className="h-underline">
                                {t("order_details-default-title")}: {data?.order?.slug}
                            </h4>
                        </Box>

                        <MediaPrint data={data} />

                        <LoadingWrapper isLoading={isLoading}>
                            {data && (
                                <div className={scss.wrapper}>
                                    <ListBlock
                                        additionalClass={scss.orderInfo}
                                        list={[
                                            ["order_details-default-order_list-slug", data?.order?.slug],
                                            ["order_details-default-order_list-created_at", convertDate(data?.order?.created_at)],
                                            ["order_details-default-order_list-company_name", data?.billing_address?.company_name],
                                            ["order_details-default-order_list-address", data?.billing_address?.address],
                                            ["order_details-default-order_list-town_name", data?.billing_address?.town_name],
                                            ["order_details-default-order_list-zip_code", data?.billing_address?.zip_code],
                                            ["order_details-default-order_list-country_name", data?.billing_address?.country_name],
                                            ["order_details-default-order_list-pib", data?.billing_address?.pib],
                                            ["order_details-default-order_list-social_security_number", data?.billing_address?.maticni_broj],
                                            ["order_details-default-order_list-orderer_of_goods", data?.billing_address?.first_name + " " + data?.billing_address?.last_name],
                                            ["order_details-default-order_list-payment_method_name", data?.order?.payment_method_name],
                                            ["order_details-default-order_list-delivery_method_name", data?.order?.delivery_method_name],
                                            ...(deliveryAdditionalText ? [["order_details-default-order_list-delivery_additional_info", deliveryAdditionalText]] : []),
                                        ]}
                                    />

                                    <div className={scss.itemsList}>
                                        <h6 className={scss.title}>{t("order_details-default-messages-ordered_products")}</h6>
                                        <div className={scss.tablePrintOverflow} style={{ maxHeight: "100%", overflow: "hidden" }}>
                                            {<DataTable actions={{ previewUrl: "/katalog/artikal/" }} columns={columns} data={products} maxHeight={"40vh"} />}
                                        </div>
                                    </div>

                                    <ListBlock title={"order_details-default-note"} list={[[null, data?.order?.note]]} additionalClass={scss.notePrint} />

                                    <ListBlock
                                        additionalClass={scss.priceBreakdown}
                                        title={"order_details-default-payment_list_title"}
                                        align="right"
                                        list={[
                                            ["order_details-default-payment_list-total_with_out_vat", currencyFormat(data?.order?.total_with_out_vat, data?.order?.currency)],
                                            ["order_details-default-payment_list-total_rebate_amount", "-" + currencyFormat(data?.order?.total_rebate_amount, data?.order?.currency)],
                                            [
                                                "order_details-default-payment_list-total_discount_amount",
                                                "-" + currencyFormat(Number(data?.order?.total_items_discount_amount) + Number(data?.order?.total_cart_discount_amount), data?.order?.currency),
                                            ],
                                            ...(Number(data?.order?.total_promo_code_amount) > 0
                                                ? [["order_details-default-payment_list-promo_code", "-" + currencyFormat(data?.order?.total_promo_code_amount, data?.order?.currency)]]
                                                : []),
                                            ["order_details-default-payment_list-subtotal", currencyFormat(data?.order?.subtotal, data?.order?.currency)],
                                            ["order_details-default-payment_list-total_vat", currencyFormat(data?.order?.total_vat, data?.order?.currency)],
                                            ["order_details-default-payment_list-total_delivery_amount", currencyFormat(data?.order?.total_delivery_amount, data?.order?.currency)],
                                            ["order_details-default-payment_list-total", currencyFormat(data?.order?.total, data?.order?.currency)],
                                        ]}
                                    />
                                    <ListBlock additionalClass={scss.duplicate} title={"order_details-default-option_list_title"}>
                                        <div className={scss.buttons}>
                                            {allowDuplicate ? (
                                                <Tooltip arrow={true} title={t("order_details-default-messages-select_products_for_cart")} placement="top">
                                                    <div className={scss.tooltipDiv}>
                                                        <Button
                                                            label={"order_details-default-button-add_to_cart"}
                                                            onClick={() => {
                                                                setShowDialog(true);
                                                                getAllProducts(duplicatedProducts?.items, duplicatedProducts);
                                                            }}
                                                            className={`${scss.duplicate} ${scss.buttonsStyle} mb-3`}
                                                            icon={"content_copy"}
                                                        />
                                                    </div>
                                                </Tooltip>
                                            ) : (
                                                <Tooltip arrow={true} title={message} placement="top">
                                                    <div className={scss.tooltipDiv}>
                                                        <Button
                                                            disabled={true}
                                                            loading={false}
                                                            label={"order_details-default-button-add_to_cart"}
                                                            onClick={() =>
                                                                toast.error(message, {
                                                                    position: "top-center",
                                                                    autoClose: 3000,
                                                                    hideProgressBar: false,
                                                                    closeOnClick: true,
                                                                    pauseOnHover: true,
                                                                    draggable: true,
                                                                })
                                                            }
                                                            className={`${scss.duplicate} ${scss.buttonsStyle} mb-3`}
                                                            icon={"content_copy"}
                                                        />
                                                    </div>
                                                </Tooltip>
                                            )}
                                        </div>

                                        <Box sx={{ display: "flex", alignItems: "center" }} className={scss.orderDupPrt}>
                                            <Tooltip arrow={true} title={t("order_details-default-messages-printing_document")} placement="top">
                                                <div className={scss.tooltipDiv}>
                                                    <Button
                                                        label={"order_details-default-button-print"}
                                                        className={`${scss.buttonsStyle} justify-content-start ${scss.printdiv}`}
                                                        onClick={() => handlePrint()}
                                                        icon={"print"}
                                                    >
                                                        <PrintIcon onClick={() => handlePrint()} sx={{ cursor: "pointer", fontSize: "2rem", color: "var(--secondary-color)" }} />
                                                    </Button>
                                                </div>
                                            </Tooltip>
                                            {/* <Button label={"Dupliraj"} onClick={() => setShowDialog(true)} className={`${scss.duplicate} ${scss.buttonsStyle}`} icon={"content_copy"} /> */}
                                            {/* <Button label={"Štampaj"} className={scss.buttonsStyle} onClick={() => handlePrint()} icon={"print"} /> */}
                                        </Box>
                                    </ListBlock>

                                    {data?.payments?.[0]?.data?.status_info?.fields?.some((field) => field.value != null && field.value !== "") && (
                                        <ListBlock
                                            additionalClass={`${scss.orderInfo} ${scss.paymentStatusInfo}`}
                                            title={"Status plaćanja"}
                                            list={data.payments[0].data.status_info.fields.map((field) => [field.label, field.value])}
                                        />
                                    )}

                                    {[
                                        data?.packages?.package_count,
                                        data?.packages?.total_quantity,
                                        data?.packages?.total_weight,
                                        data?.packages?.total_gross_weight,
                                        data?.packages?.action_packaging_profile_name,
                                    ].some((value) => value != null && value !== "") && (
                                        <ListBlock
                                            additionalClass={`${scss.orderInfo} ${scss.packagingInfo}`}
                                            title={"Podaci o pakovanju"}
                                            list={[
                                                ["Broj paketa", data.packages.package_count],
                                                ["Ukupna količina", data.packages.total_quantity],
                                                ["Ukupna težina (kg)", data.packages.total_weight],
                                                ["Bruto težina (kg)", data.packages.total_gross_weight],
                                                ["Tip pakovanja", data.packages.action_packaging_profile_name],
                                            ]}
                                        />
                                    )}
                                </div>
                            )}
                        </LoadingWrapper>
                    </div>
                </div>
                <DeleteDialog
                    loadingwidth={scss.loadingwidth}
                    openDeleteDialog={{ show: showDialog }}
                    selectedProducts={selectedProducts}
                    loading={loading}
                    getAllProducts={getAllProducts}
                    products={duplicatedProducts?.items}
                    setOpenDeleteDialog={() => setShowDialog(false)}
                    handleConfirm={async () => {
                        const checkQuantityNullValues = selectedProducts?.filter((product) => product?.quantity === null);

                        if (selectedProducts?.length > 0 && checkQuantityNullValues?.length === 0) {
                            setLoading(true);
                            await api.put(`/previous-orders/duplicate/confirm`, { cart_items: selectedProducts }).then((res) => {
                                res?.add_items?.length > 0 &&
                                    toast.success(t("order_details-default-messages-success_added_product", { count: res?.add_items?.length }), {
                                        position: "top-center",
                                        autoClose: 3000,
                                        hideProgressBar: false,
                                        closeOnClick: true,
                                        pauseOnHover: true,
                                        draggable: true,
                                    });

                                res?.error_items?.length > 0 &&
                                    res?.error_items?.forEach((item) => {
                                        toast.success(item?.message, {
                                            position: "top-center",
                                            autoClose: 3000,
                                            hideProgressBar: false,
                                            closeOnClick: true,
                                            pauseOnHover: true,
                                            draggable: true,
                                        });
                                    });

                                setShowDialog(false);

                                setSelectedProducts(
                                    duplicatedProducts?.items?.map((item) => {
                                        const isAddable = item?.allow_add;
                                        if (isAddable) {
                                            return item?.cart;
                                        }
                                    })
                                );
                                setTimeout(() => {
                                    setLoading(false);
                                }, 500);
                            });
                        } else {
                            if (checkQuantityNullValues?.length > 0) {
                                toast.warn("Niste uneli količinu za sve proizvode.", {
                                    position: "top-center",
                                    autoClose: 3000,
                                    hideProgressBar: false,
                                    closeOnClick: true,
                                    pauseOnHover: true,
                                    draggable: true,
                                });
                            } else {
                                toast.error("Niste izabrali nijedan proizvod.", {
                                    position: "top-center",
                                    autoClose: 3000,
                                    hideProgressBar: false,
                                    closeOnClick: true,
                                    pauseOnHover: true,
                                    draggable: true,
                                });
                            }
                        }
                    }}
                    title={"order_details-default-delete_dialog-title"}
                    description={"order_details-default-delete_dialog-description"}
                    nameOfButtonDelete={"order_details-default-delete_dialog-button"}
                    deafultIcon={false}
                    setSelectedProducts={setSelectedProducts}
                >
                    <i
                        onClick={() => {
                            setShowDialog(false);
                            setSelectedProducts([]);
                        }}
                        className={`fa fa-solid fa-times ${scss.exit}`}
                    ></i>
                    {duplicatedProducts?.items?.map((item, index) => {
                        const isAddable = item?.allow_add;
                        const isAdded = selectedProducts?.find((product) => product?.id_product === item?.cart?.id_product);

                        const basicData = productQueries[index]?.data;
                        let behaviours;
                        let inventory;
                        if (basicData?.data?.variant_items.length > 0) {
                            const currentVariant = basicData?.data?.variant_items.find((variantItem) => variantItem.id == item.product.id);
                            behaviours = currentVariant.behaviours;
                            inventory = currentVariant.inventory;
                        } else {
                            behaviours = basicData?.data?.item?.behaviours;
                            inventory = basicData?.data?.item?.inventory;
                        }

                        return (
                            <div key={index}>
                                {isAddable && (
                                    <div className={`${scss.modalproduct} ${isAdded ? scss.added : scss.notAdded}`}>
                                        <div
                                            className={`flex align-items-center gap-2 cursor-pointer ${scss.cursor}`}
                                            onClick={(e) => {
                                                if (!isAdded) {
                                                    setSelectedProducts([...selectedProducts, item?.cart]);
                                                } else {
                                                    setSelectedProducts(selectedProducts.filter((product) => product?.id_product !== item?.cart?.id_product));
                                                }
                                            }}
                                        >
                                            <img src={item?.product?.image} alt="" width={50} height={50} />
                                            <p
                                                style={{
                                                    margin: 0,
                                                }}
                                            >
                                                {item?.product?.name} ({item?.product?.sku})
                                            </p>
                                        </div>
                                        <PlusMinusInput
                                            className={scss.quantity}
                                            behaviours={behaviours?.cart}
                                            max={Number(inventory?.amount)}
                                            quantity={Number(item?.cart?.quantity)}
                                            setQuantity={(quantity) => {
                                                setSelectedProducts(
                                                    selectedProducts.map((product) => {
                                                        if (product?.id_product === item?.cart?.id_product) {
                                                            product.quantity = quantity;
                                                        }
                                                        return product;
                                                    })
                                                );
                                            }}
                                        />
                                    </div>
                                )}
                            </div>
                        );
                    })}
                </DeleteDialog>
            </MainCard>
        </>
    );
};

export default OrdersDetailsPage;
